import {
  canAccessDepartmentDashboard,
  departmentForRole,
  departmentLabel,
  departmentPermission,
  departmentWorkspacePath,
  homePathForRole,
  isDepartmentForeman,
  isDepartmentRole,
  isGuardRole,
  isLabRole,
  isProductionLoadingSupervisorRole,
  isWeighbridgeRole,
} from "./dashboardRole";

/** Page routes require these permission codes (must match backend rbac/catalog.py). */
export const ROUTE_PERMISSIONS = {
  "/": "page.dashboard",
  "/daily-report": "page.dashboard",
  "/gate": "page.gate",
  "/gate/procurement": "page.gate",
  "/gate/outward": "page.gate",
  "/gate/visitors": "page.gate",
  "/weighbridge": "page.weighbridge",
  "/weighbridge/inward": "page.weighbridge",
  "/weighbridge/outward": "page.weighbridge",
  "/weighbridge/register": "page.weighbridge",
  "/contracts": "page.contracts",
  "/sales-contracts": "page.sales_contracts",
  "/sales/link": "page.outward_dispatch",
  "/sales/loading": "page.outward_dispatch",
  "/sales/vouchers": "page.outward_dispatch",
  "/sales/reports": "page.sales_reports",
  "/outward-dispatch": "page.outward_dispatch",
  "/outward-loading": "page.outward_dispatch",
  "/purchases": "page.purchases",
  "/procurement/general": "page.general_procurement",
  "/store/grn": "page.store_keeper",
  "/purchase/reports": "page.purchase_reports",
  "/purchase/vouchers": "page.accounts",
  "/stack-sample-intake": "page.stack_samples",
  "/stack-sample-approval": "page.contracts",
  "/lab": "page.lab",
  "/lab/truck": "page.lab",
  "/lab/composite": "page.lab",
  "/lab/plant": "page.lab",
  "/lab/stacks": "page.lab",
  "/unloading": "page.unloading",
  "/stacks": "page.stacks",
  "/production": "page.production",
  "/production/packing-register": "page.production_packing_register",
  "/production/reprocessing": "page.production_reprocessing",
  "/production/batches": "page.production",
  "/production/batch-masters": "page.lookups",
  "/production/boiler": "page.production_boiler",
  "/production/boiler/dashboard": "page.production_boiler",
  "/production/boiler/register": "page.production_boiler",
  "/production/parboiled": "page.production_parboiled",
  "/production/parboiled/dashboard": "page.production_parboiled",
  "/production/milling": "page.production_milling",
  "/production/milling/dashboard": "page.production_milling",
  "/production/team": "production.foreman.team.view",
  "/accounts": "page.accounts",
  "/accounting": "page.accounts",
  "/accounting/tally-mapping": "page.accounts",
  "/accounts/tally-approvals": "tally.sync.approve",
  "/accounts/tally-mapping": "page.accounts",
  "/accounts/bank-reconciliation": "page.accounts",
  "/accounts/tds": "page.accounts",
  "/accounts/ledger": "page.accounts",
  "/labour-contracts": "page.labour_contracts",
  "/labour-warehouse": "page.labour_warehouse",
  "/parties": "page.parties",
  "/parties/new": "page.parties",
  "/users": "page.users",
  "/audit-logs": "page.audit_logs",
  "/dropdown-choices": "page.lookups",
  "/paddy-quality-lab": "page.quality_lab",
};

export function permissionForPath(pathname) {
  if (ROUTE_PERMISSIONS[pathname]) return ROUTE_PERMISSIONS[pathname];
  if (pathname.startsWith("/gate/")) return "page.gate";
  if (pathname.startsWith("/store/")) return "page.store_keeper";
  if (pathname.startsWith("/production/")) {
    return ROUTE_PERMISSIONS[pathname] || "page.production";
  }
  if (pathname.startsWith("/weighbridge/")) return "page.weighbridge";
  if (pathname.startsWith("/lab/")) return "page.lab";
  if (pathname.startsWith("/purchase/reports")) return "page.purchase_reports";
  if (pathname.startsWith("/purchase/")) {
    return ROUTE_PERMISSIONS[pathname] || "page.purchases";
  }
  if (pathname.startsWith("/purchases/")) return "page.purchases";
  if (pathname.startsWith("/sales/reports")) return "page.sales_reports";
  if (pathname.startsWith("/sales/")) {
    return ROUTE_PERMISSIONS[pathname] || "page.outward_dispatch";
  }
  if (/^\/parties\/[^/]+\/ledger\/?$/.test(pathname)) return "page.accounts";
  if (pathname.startsWith("/parties/")) return "page.parties";
  if (pathname.startsWith("/accounts/")) return "page.accounts";
  return null;
}

function isDepartmentDashboardPath(to) {
  return /^\/production\/(boiler|parboiled|milling)\/dashboard$/.test(to);
}

function navItemVisible(user, item, { hasPermission, isAdmin }) {
  if (!hasPermission(user, item.permission)) return false;
  if (item.adminOnly && !isAdmin(user)) return false;
  if (item.foremanOnly || isDepartmentDashboardPath(item.to)) {
    const dept = item.to.match(/^\/production\/(\w+)\/dashboard$/)?.[1];
    if (!dept || !canAccessDepartmentDashboard(user.role, dept)) return false;
  }
  return true;
}

/**
 * Sidebar navigation: top-level groups (tabs) with child links (sub-tabs).
 * Match backend rbac/catalog.py page permissions.
 */
export const NAV_GROUPS = [
  {
    id: "overview",
    label: "Overview",
    items: [
      { to: "/", iconKey: "dashboard", label: "Dashboard", permission: "page.dashboard" },
      { to: "/daily-report", iconKey: "reports", label: "Daily mill report", permission: "page.purchase_reports" },
    ],
  },
  {
    id: "gate",
    label: "Gate",
    items: [
      { to: "/gate/procurement", iconKey: "gate", label: "Procurement (Inward)", permission: "page.gate" },
      { to: "/gate/store", iconKey: "gate", label: "Store inward", permission: "page.gate" },
      { to: "/gate/outward", iconKey: "gate", label: "Outward Supply", permission: "page.gate" },
      { to: "/gate/visitors", iconKey: "gate", label: "Visitors", permission: "page.gate" },
    ],
  },
  {
    id: "weighbridge",
    label: "Weighbridge",
    items: [
      { to: "/weighbridge/inward", iconKey: "weighbridge", label: "Inward", permission: "page.weighbridge" },
      { to: "/weighbridge/outward", iconKey: "weighbridge", label: "Outward", permission: "page.weighbridge" },
      { to: "/weighbridge/register", iconKey: "audit", label: "Register", permission: "page.weighbridge" },
    ],
  },
  {
    id: "purchase",
    label: "Purchase",
    items: [
      { to: "/contracts", iconKey: "contracts", label: "Purchase Contracts", permission: "page.contracts" },
      { to: "/unloading", iconKey: "unloading", label: "Unloading", permission: "page.unloading" },
      { to: "/stack-sample-intake", iconKey: "stack_samples", label: "Stack Sample Intake", permission: "page.stack_samples" },
      { to: "/stack-sample-approval", iconKey: "contracts", label: "Sample Approval", permission: "page.contracts" },
      { to: "/purchases", iconKey: "purchases", label: "Purchases", permission: "page.purchases" },
      { to: "/procurement/general", iconKey: "purchases", label: "General procurement", permission: "page.general_procurement" },
      { to: "/purchase/vouchers", iconKey: "accounts", label: "Purchase Voucher", permission: "page.accounts" },
      { to: "/purchase/reports", iconKey: "reports", label: "Reporting", permission: "page.purchase_reports" },
    ],
  },
  {
    id: "sales",
    label: "Sales",
    items: [
      { to: "/sales-contracts", iconKey: "contracts", label: "Sales Contract", permission: "page.sales_contracts" },
      { to: "/sales/link", iconKey: "purchases", label: "Sales Link", permission: "page.outward_dispatch" },
      { to: "/sales/loading", iconKey: "unloading", label: "Loading", permission: "page.outward_dispatch" },
      { to: "/sales/vouchers", iconKey: "accounts", label: "Sales Voucher", permission: "page.outward_dispatch" },
      { to: "/sales/reports", iconKey: "reports", label: "Sales Reporting", permission: "page.sales_reports" },
    ],
  },
  {
    id: "lab",
    label: "Lab",
    items: [
      { to: "/lab/truck", iconKey: "lab", label: "Truck Arrival Report", permission: "page.lab" },
      { to: "/lab/composite", iconKey: "lab", label: "Composite Report", permission: "page.lab" },
      { to: "/lab/plant", iconKey: "lab", label: "In Plant Report", permission: "page.lab" },
      { to: "/lab/stacks", iconKey: "lab", label: "Stack Sample Report", permission: "page.lab" },
    ],
  },
  {
    id: "inventory",
    label: "Inventory",
    items: [
      { to: "/stacks", iconKey: "stacks", label: "Stacks / Inventory", permission: "page.stacks" },
      { to: "/store/grn", iconKey: "unloading", label: "Store keeper GRN", permission: "page.store_keeper" },
    ],
  },
  {
    id: "production",
    label: "Production",
    items: [
      { to: "/production/boiler/dashboard", iconKey: "dashboard", label: "Boiler dashboard", permission: "page.production_boiler", foremanOnly: true },
      { to: "/production/boiler", iconKey: "production", label: "Boiler workspace", permission: "page.production_boiler" },
      { to: "/production/boiler/register", iconKey: "audit", label: "Boiler log register", permission: "page.production_boiler" },
      { to: "/production/parboiled/dashboard", iconKey: "dashboard", label: "Parboiled dashboard", permission: "page.production_parboiled", foremanOnly: true },
      { to: "/production/parboiled", iconKey: "production", label: "Parboiled workspace", permission: "page.production_parboiled" },
      { to: "/production/milling/dashboard", iconKey: "dashboard", label: "Milling dashboard", permission: "page.production_milling", foremanOnly: true },
      { to: "/production/milling", iconKey: "production", label: "Milling workspace", permission: "page.production_milling" },
      { to: "/production/team", iconKey: "users", label: "My team", permission: "production.foreman.team.view" },
      { to: "/production", iconKey: "production", label: "Yield log", permission: "page.production" },
      { to: "/production/packing-register", iconKey: "production", label: "Packing register", permission: "page.production_packing_register" },
      { to: "/production/reprocessing", iconKey: "production", label: "Reprocessing & blending", permission: "page.production_reprocessing" },
      { to: "/production/batches", iconKey: "production", label: "Batch controller", permission: "page.production", adminOnly: true },
      { to: "/production/batch-masters", iconKey: "lookups", label: "Batch masters", permission: "page.lookups", adminOnly: true },
    ],
  },
  {
    id: "finance",
    label: "Finance",
    items: [
      { to: "/accounts/tally-approvals", iconKey: "accounts", label: "Tally approvals", permission: "tally.sync.approve" },
      { to: "/accounts", iconKey: "accounts", label: "Accounts", permission: "page.accounts" },
      { to: "/accounts/ledger", iconKey: "accounts", label: "General ledger", permission: "page.accounts" },
      { to: "/accounts/bank-reconciliation", iconKey: "accounts", label: "Bank reconciliation", permission: "page.accounts" },
      { to: "/accounts/tally-mapping", iconKey: "accounts", label: "Tally mapping", permission: "page.accounts" },
      { to: "/labour-contracts", iconKey: "contracts", label: "Labour Contracts", permission: "page.labour_contracts" },
      { to: "/labour-warehouse", iconKey: "stacks", label: "Labour & warehouse", permission: "page.labour_warehouse" },
    ],
  },
  {
    id: "master",
    label: "Master Data",
    items: [
      { to: "/parties", iconKey: "parties", label: "Parties", permission: "page.parties" },
    ],
  },
  {
    id: "admin",
    label: "Administration",
    items: [
      { to: "/users", iconKey: "users", label: "Users & Access", permission: "page.users", adminOnly: true },
      { to: "/audit-logs", iconKey: "audit", label: "Audit log", permission: "page.audit_logs", adminOnly: true },
      { to: "/dropdown-choices", iconKey: "lookups", label: "Dropdown choices", permission: "page.lookups", adminOnly: true },
      { to: "/paddy-quality-lab", iconKey: "lab", label: "Quality Lab Params", permission: "page.quality_lab", adminOnly: true },
    ],
  },
];

/** Flat list for bottom nav and legacy callers. */
export const NAV_ITEMS = NAV_GROUPS.flatMap((group) => group.items);

function navGroupsForDepartmentUser(user, { hasPermission }) {
  const dept = departmentForRole(user.role);
  const permission = departmentPermission(dept);
  if (!dept || !permission || !hasPermission(user, permission)) return [];

  const items = [];

  if (isDepartmentForeman(user.role)) {
    items.push({
      to: homePathForRole(user.role),
      iconKey: "dashboard",
      label: "Dashboard",
      permission,
    });
  }

  if (dept === "boiler") {
    items.push({
      to: "/production/boiler/register",
      iconKey: "audit",
      label: "Log register",
      permission,
    });
  }

  items.push({
    to: departmentWorkspacePath(dept),
    iconKey: "production",
    label: "Workspace",
    permission,
  });

  if (hasPermission(user, "production.foreman.team.view")) {
    items.push({
      to: "/production/team",
      iconKey: "users",
      label: "My team",
      permission: "production.foreman.team.view",
    });
  }

  return [{ id: "department", label: departmentLabel(dept), items }];
}

function navGroupsForGuardUser(user, { hasPermission }) {
  const items = [
    { to: "/", iconKey: "dashboard", label: "Home", permission: "page.dashboard" },
    { to: "/gate/procurement", iconKey: "gate", label: "Inward", permission: "page.gate", mobilePrimary: true },
    { to: "/gate/store", iconKey: "gate", label: "Store", permission: "page.gate" },
    { to: "/gate/outward", iconKey: "gate", label: "Outward", permission: "page.gate" },
    { to: "/gate/visitors", iconKey: "gate", label: "Visitor", permission: "page.gate" },
  ].filter((item) => hasPermission(user, item.permission));

  return items.length ? [{ id: "gate", label: "Gate", items }] : [];
}

function navGroupsForLabUser(user, { hasPermission }) {
  const items = [
    { to: "/", iconKey: "dashboard", label: "Home", permission: "page.dashboard" },
    { to: "/lab/truck", iconKey: "lab", label: "Truck Arrival", permission: "page.lab" },
    { to: "/lab/composite", iconKey: "lab", label: "Composite", permission: "page.lab", mobilePrimary: true },
    { to: "/lab/plant", iconKey: "lab", label: "In Plant", permission: "page.lab" },
    { to: "/lab/stacks", iconKey: "lab", label: "Stack Sample", permission: "page.lab" },
  ].filter((item) => hasPermission(user, item.permission));

  return items.length ? [{ id: "lab", label: "Lab", items }] : [];
}

function navGroupsForWeighbridgeUser(user, { hasPermission }) {
  const items = [
    { to: "/", iconKey: "dashboard", label: "Home", permission: "page.dashboard" },
    { to: "/weighbridge/inward", iconKey: "weighbridge", label: "Inward", permission: "page.weighbridge", mobilePrimary: true },
    { to: "/weighbridge/outward", iconKey: "weighbridge", label: "Outward", permission: "page.weighbridge" },
    { to: "/weighbridge/register", iconKey: "audit", label: "Register", permission: "page.weighbridge" },
  ].filter((item) => hasPermission(user, item.permission));

  return items.length ? [{ id: "weighbridge", label: "Weighbridge", items }] : [];
}

function navGroupsForProductionLoadingSupervisor(user, { hasPermission }) {
  const items = [
    { to: "/", iconKey: "dashboard", label: "Dashboard", permission: "page.dashboard", shortLabel: "Home" },
    { to: "/sales/loading", iconKey: "purchases", label: "Loading", permission: "page.outward_dispatch", mobilePrimary: true, shortLabel: "Loading" },
    { to: "/production/packing-register", iconKey: "production", label: "Packing", permission: "page.production_packing_register", shortLabel: "Packing" },
    { to: "/store/grn", iconKey: "unloading", label: "Store GRN", permission: "page.store_keeper", shortLabel: "Store" },
    { to: "/stacks", iconKey: "stacks", label: "Stock on hand", permission: "page.stacks", shortLabel: "Stock" },
  ].filter((item) => hasPermission(user, item.permission));

  return items.length
    ? [{ id: "packing_loading", label: "Packing & Loading", items }]
    : [];
}

export function navGroupsForUser(user, { hasPermission, isAdmin }) {
  if (!user) return [];

  // Multiple roles → full permission-based nav (e.g. weighbridge + accounts).
  const roleCount = Array.isArray(user.roles) && user.roles.length
    ? user.roles.length
    : 1 + (Array.isArray(user.extra_roles) ? user.extra_roles.length : 0);
  if (roleCount > 1) {
    return NAV_GROUPS.map((group) => ({
      ...group,
      items: group.items.filter((item) => navItemVisible(user, item, { hasPermission, isAdmin })),
    })).filter((group) => group.items.length > 0);
  }

  if (isGuardRole(user.role)) {
    return navGroupsForGuardUser(user, { hasPermission });
  }
  if (isLabRole(user.role)) {
    return navGroupsForLabUser(user, { hasPermission });
  }
  if (isWeighbridgeRole(user.role)) {
    return navGroupsForWeighbridgeUser(user, { hasPermission });
  }
  if (isProductionLoadingSupervisorRole(user.role)) {
    return navGroupsForProductionLoadingSupervisor(user, { hasPermission });
  }
  if (isDepartmentRole(user.role)) {
    return navGroupsForDepartmentUser(user, { hasPermission });
  }
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => navItemVisible(user, item, { hasPermission, isAdmin })),
  })).filter((group) => group.items.length > 0);
}

export function flattenNavGroups(groups) {
  return groups.flatMap((group) => group.items);
}

/** True when the user only has one module tab — show sub-links directly on mobile/desktop. */
export function hasSingleNavGroup(groups) {
  return groups.length === 1;
}

export function primaryNavItems(groups) {
  if (hasSingleNavGroup(groups)) return groups[0].items;
  return flattenNavGroups(groups);
}
